import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MovingIcon from "@mui/icons-material/Moving";
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import { NavLink } from "react-router-dom";
import SelectLanguageComponent from "./SelectLanguageComponent";
import api from "../config/axios"; // <-- Your axios instance with interceptors

// This remains the same:
const sidebarMenuButtons = [
  {
    text: "Account",
    currentIcon: "/icons/accountIconBlack.svg",
    activeIcon: "/icons/accountIconYellow.svg",
    link: "/account",
  },
  {
    text: "Notification",
    currentIcon: "/icons/notificationSettingIconBlack.svg",
    activeIcon: "/icons/notificationSettingIconYellow.svg",
    link: "/notifications-setting",
  },
  {
    text: "Privacy & Safety",
    currentIcon: "/icons/privacyIconBlack.svg",
    activeIcon: "/icons/privacyIconYellow.svg",
    link: "/privacy-and-safety",
  },
  {
    text: "Communications",
    currentIcon: "/icons/communicationIconBlack.svg",
    activeIcon: "/icons/communicationIconYellow.svg",
    link: "/communications",
  },

  {
    text: "Messaging",
    currentIcon: "/icons/jobsIcon.svg",
    activeIcon: "/icons/messagingIconYellow.svg",
    link: "/messaging-setting",
  },
  {
    text: "Close Account",
    currentIcon: "/icons/notificationsIcon.svg",
    activeIcon: "/icons/closeAccountYellow.svg",
    link: "/close-account",
  },
  {
    text: "Profile Information",
    currentIcon: "/icons/ProfileInformationIcon.svg",
    activeIcon: "/icons/ProfileInformationIconYellow.svg",
    link: "/profile-information",
  },
  {
    text: "Display",
    currentIcon: "/icons/displayIcon.svg",
    activeIcon: "/icons/displayIconYellow.svg",
    link: "/display",
  },
  {
    text: "General Preferences",
    currentIcon: "/icons/generalPreferencesIcon.svg",
    activeIcon: "/icons/generalPreferencesIconYellow.svg",
    link: "/general-preferences",
  },
  {
    text: "Community",
    currentIcon: <PeopleOutlinedIcon sx={{ color: "#000000", width: "20px", height: "20px", pl: "6px" }} />,
    activeIcon: <PeopleOutlinedIcon sx={{ color: "#FDDF59", width: "20px", height: "20px", pl: "6px" }} />,
    link: "/community",
  },
  // {
  //   text: "Community Info",
  //   // currentIcon: "/icons/generalPreferencesIcon.svg",
  //   // activeIcon: "/icons/generalPreferencesIconYellow.svg",
  //   link: "/community-inner",
  // },
  {
    text: "Group",
    currentIcon: <GroupAddOutlinedIcon sx={{ color: "#000000", width: "20px", height: "20px", pl: "6px" }} />,
    activeIcon: <GroupAddOutlinedIcon sx={{ color: "#FDDF59", width: "20px", height: "20px", pl: "6px" }} />,
    link: "/community-group-form",
  },
  {
    text: "Event",
    currentIcon: <EventOutlinedIcon sx={{ color: "#000000", width: "20px", height: "20px", pl: "6px" }} />,
    activeIcon: <EventOutlinedIcon sx={{ color: "#FDDF59", width: "20px", height: "20px", pl: "6px" }} />,
    link: "/community-event-form",
  },
  {
    text: "Companies",
    currentIcon: <AddBusinessOutlinedIcon sx={{ color: "#000000", width: "20px", height: "20px", pl: "6px" }} />,
    activeIcon: <AddBusinessOutlinedIcon sx={{ color: "#FDDF59", width: "20px", height: "20px", pl: "6px" }} />,
    link: "/companies",
  },
];

function AppLeftSidebar() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [profile, setProfile] = useState(null); // For user data
  const [allChannels, setAllChannels] = useState([]); // If you want to list all channels
  // or if you only want "subscribed" channels from the user, see below.

  useEffect(() => {
    // 1) Fetch current user data
    api
      .get("/auth/me")
      .then((res) => {
        setProfile(res.data);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
      });

    // 2) Fetch all channels (if you want to show them or allow user to subscribe)
    api
      .get("/channels")
      .then((res) => {
        setAllChannels(res.data);
      })
      .catch((err) => {
        console.error("Error fetching channels:", err);
      });
  }, []);

  // Optional: If data not loaded yet, show a quick fallback
  if (!profile) {
    return <div>Loading sidebar...</div>;
  }

  // Build dynamic stats from the user's data
  const dynamicProfileStats = [
    { count: profile.friendsCount || 0, statName: "Friends" },
    { count: profile.followersCount || 0, statName: "Followers" },
    { count: profile.followingCount || 0, statName: "Following" },
  ];

  // If you only want to show the user's subscribed channels, use:
  // const subscribedChannels = profile.subscribedChannels || [];
  // If you want to show all channels from the server, use `allChannels`.

  return (
    <Stack
      id="left-sidebar"
      sx={{
        bgcolor: "#ffffff",
        borderRight: "2px solid #d9d9d9",
        borderBottom: "2px solid #d9d9d9",
        p: "6px",
      }}
    >
      {/* Top Profile Section */}
      <Stack
        id="robert-fox"
        direction="column"
        sx={{ p: "10px", justifyContent: "center", alignItems: "center" }}
      >
        {/* "Pitchers" logo */}
        <Stack
          id="pitchers-logo-name"
          direction="row"
          alignSelf="start"
          sx={{ pb: "30px" }}
        >
          <Box
            component="img"
            src="/images/pitchers.png"
            sx={{ width: "32px", height: "32px", borderRadius: "6px" }}
          />
          <Typography variant="h6" sx={{ pl: "10px" }}>
            Pitchers
          </Typography>
        </Stack>

        {/* Cover image with avatar in center */}
        <Box
          sx={{
            backgroundImage: `url(${
              profile.coverImage || "/images/desert-pattern.jpeg"
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: "245px",
            height: "100px",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Avatar
            src={profile.profileImage || "/images/navbar-profile-pic.jpeg"}
            alt="Profile Picture"
            sx={{ width: 52, height: 52, objectFit: "cover" }}
          />
        </Box>

        {/* Name & Verified */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            lineHeight: "20px",
            mt: "4px",
          }}
        >
          {/* You can show firstName + lastName or fallback to "Robert Fox" */}
          {profile.firstName || "Robert"} {profile.lastName || "Fox"}{" "}
          <VerifiedIcon
            sx={{
              color: "#007dfc",
              width: "14px",
              height: "14px",
            }}
          />
        </Typography>

        {/* Job Title */}
        <Typography
          variant="body2"
          sx={{ color: "#8c8c8c", fontSize: "8px", mb: "6px" }}
        >
          {profile.jobTitle || "UI / UX Designer"}
        </Typography>

        {/* Stats */}
        <Stack id="profile-stats" direction="row" gap={3}>
          {dynamicProfileStats.map((stat, i) => (
            <Stack
              key={i}
              direction={"column"}
              justifyContent={"center"}
              alignItems="center"
            >
              <Typography variant="subtite2" sx={{ fontWeight: "bold" }}>
                {stat.count}
              </Typography>
              <Typography variant="body2" sx={{ color: "#8c8c8c" }}>
                {stat.statName}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* Tagline */}
        <Typography variant="body2" sx={{ textAlign: "center", py: "12px" }}>
          {profile.tagline ||
            "I'd love to change the world, but they won't give me the source code"}
        </Typography>
      </Stack>

      {/* Search Field */}
      <TextField
        variant="outlined"
        placeholder="Search or type a command"
        fullWidth
        margin="-10px"
        sx={{
          input: {
            bgcolor: "#f5f5f5",
            color: "#cbcddc",
            fontSize: "12px",
            boxSizing: "content-box",
            px: "0px",
            py: "15px",
            "&::placeholder": {
              color: "#cbcdd3",
              opacity: 1,
            },
          },
          "& .MuiOutlinedInput-root": {
            px: "8px",
            borderRadius: "8px",
            bgcolor: "#f5f5f5",
            "& fieldset": {
              borderColor: "#cfcfcf",
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: "#cfcfcf",
              borderWidth: "1px",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#cfcfcf",
              borderWidth: "1px",
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment sx={{ pr: "6px" }} position="start">
              <SearchIcon sx={{ color: "#a2a5b1" }} />
            </InputAdornment>
          ),
        }}
      />

      <Divider
        sx={{
          height: ".5px",
          bgcolor: "#e2e4e7",
          mx: "-8px",
          my: "30px",
          border: "none",
        }}
      />

      {/* Sidebar Menu Buttons (unchanged, except dynamic highlight) */}
      <Stack spacing={1} sx={{}}>
        {sidebarMenuButtons.map((menuBtn, i) => {
          const isActive = i === activeIndex;
          return (
            <NavLink to={menuBtn.link}>
              <Button
                variant="text"
                startIcon={
                  typeof menuBtn.currentIcon === 'string' ? (
                    <Box
                      component="img"
                      src={isActive ? menuBtn.activeIcon : menuBtn.currentIcon}
                      sx={{ width: "20px", height: "20px", pl: "6px" }}
                    />
                  ) : (
                    isActive ? menuBtn.activeIcon : menuBtn.currentIcon
                  )
                }
                onClick={() => setActiveIndex(i)}
                sx={{
                  color: isActive ? "#FDDF59" : "#000000",
                  textTransform: "none",
                  width: isActive ? "100%" : "100%",
                  textAlign: isActive ? "start" : "",
                  "& .MuiButton-startIcon": {
                    m: "0px",
                    mr: "12px",
                  },
                  bgcolor: isActive ? "#272727" : "none",
                  borderRadius: "12px",
                  py: isActive ? "14px" : "0px",
                  pr: "14px",
                  fontSize: "15px",
                  justifyContent: "start",
                }}
              >
                {menuBtn.text}
              </Button>
            </NavLink>
          );
        })}
      </Stack>

      <Divider
        sx={{
          height: ".5px",
          bgcolor: "#e2e4e7",
          mx: "-8px",
          my: "30px",
          border: "none",
        }}
      />

      {/* Profile Activity (still static example) */}
      <Stack id="profile-activity-1158 followers" sx={{ p: "10px", pt: "0px" }}>
        <Stack
          id="profile-activity"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          pb="25px"
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Profile Activity
          </Typography>

          <IconButton sx={{ p: "0px" }}>
            <MoreVertIcon sx={{ color: "#cbd5e1" }} />
          </IconButton>
        </Stack>

        <Stack
          id="1158-followers"
          direction="column"
          sx={{
            bgcolor: "#f6f6f6",
            py: "20px",
            px: "14px",
            borderRadius: "26px",
          }}
        >
          <Stack direction="row" spacing={-1.5}>
            {/** Some dummy avatar placeholders */}
            {[...Array(7)].map((_, index) => (
              <Avatar
                key={index}
                sx={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid white",
                  bgcolor: "#e0e0e0",
                }}
              />
            ))}
          </Stack>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              alignItems: "center",
              display: "flex",
              pt: "20px",
            }}
          >
            +1,158{" "}
            <span
              style={{
                color: "#475569",
                fontSize: "16px",
                fontWeight: "lighter",
                paddingLeft: "6px",
              }}
            >
              Followers
            </span>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#22C55E",
              pb: "16px",
            }}
          >
            <MovingIcon sx={{ pr: "4px" }} />
            23%{" "}
            <span style={{ color: "#475569", paddingLeft: "4px" }}>
              vs last month
            </span>
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            You gained a substantial amount of followers this month!
          </Typography>
        </Stack>
      </Stack>

      {/* Subscribe Channel */}
      <Stack id="Subscribe channel" sx={{ px: "12px" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", pb: "16px" }}>
          Subscribe Channel
        </Typography>

        {/* 
          Option A: Show all channels from the DB 
          (If you only want the user's subscribed channels, replace with profile.subscribedChannels)
        */}
        {allChannels.map((channel, i) => (
          <Stack key={channel._id} direction="row" alignItems="center" sx={{ pb: "20px" }}>
            <Box
              component="img"
              src={channel.icon || "/images/pitchers.png"}
              sx={{
                width: "30px",
                height: "30px",
                borderRadius: "6px",
                bgcolor: "#d9d9d9",
                opacity: "1",
              }}
            />
            <Button
              variant="text"
              sx={{
                p: "0px",
                textTransform: "none",
                margin: "0px",
                pl: "10px",
                color: "#000000",
              }}
            >
              {channel.name}
            </Button>
          </Stack>
        ))}

        {/* Or you can show user-subscribed channels specifically: 
            profile.subscribedChannels?.map(...)
        */}

        <SelectLanguageComponent />
      </Stack>
    </Stack>
  );
}

export default AppLeftSidebar;